import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { analytics } from '@/analytics';
import Search from '@/assets/icons/search_line.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { ModalBody, ModalComponent } from '@/components/molecules/ModalComponent';

import QuickSearchRow from './QuickSearchRow';
import { filterAndSortQuickSearchItems } from './quickSearchHelpers';
import type { QuickSearchItem } from './types';
import { useQuickSearchItems } from './useQuickSearchItems';
import { useQuickSearchRemoteItems } from './useQuickSearchRemoteItems';

import styles from './QuickSearch.module.scss';

export interface QuickSearchProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function QuickSearch(props: QuickSearchProps) {
    const { isOpen, onClose } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const items = useQuickSearchItems(isOpen);
    const [search, setSearch] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const remote = useQuickSearchRemoteItems(isOpen, search);

    useEffect(() => {
        if (isOpen) {
            setSearch('');
            setActiveIndex(0);
            const timeoutId = setTimeout(() => {
                inputRef.current?.focus();
            }, 150);
            return () => clearTimeout(timeoutId);
        }
    }, [isOpen]);

    const filteredItems = useMemo(
        () => filterAndSortQuickSearchItems(items, search, remote.items),
        [items, search, remote.items]
    );

    useEffect(() => {
        setActiveIndex((prev) => {
            if (filteredItems.length === 0) return 0;
            return Math.min(prev, filteredItems.length - 1);
        });
    }, [filteredItems.length]);

    const handleSelect = useCallback(
        (item: QuickSearchItem) => {
            if (!item?.url) return;
            analytics.quickSearchResultSelected({
                queryLength: search.trim().length,
                resultKind: item.kind,
                resultGroup: item.group,
                isIdMatch: Boolean(item.isIdMatch),
                position: filteredItems.findIndex((candidate) => candidate.id === item.id),
                resultCount: filteredItems.length,
            });
            navigate(item.url);
            onClose();
        },
        [filteredItems, navigate, onClose, search]
    );

    const groupedItems = useMemo(() => {
        const groups: Record<
            string,
            { items: Array<{ item: QuickSearchItem; globalIndex: number }> }
        > = {};

        filteredItems.forEach((item, globalIndex) => {
            const groupName = item.group;
            if (!groups[groupName]) {
                groups[groupName] = { items: [] };
            }
            groups[groupName].items.push({ item, globalIndex });
        });

        return groups;
    }, [filteredItems]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (filteredItems.length === 0) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveIndex((prev) => (prev + 1) % filteredItems.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIndex((prev) => (prev - 1 + filteredItems.length) % filteredItems.length);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                const activeItem = filteredItems[activeIndex];
                if (activeItem) {
                    handleSelect(activeItem);
                }
            }
        },
        [filteredItems, activeIndex, handleSelect]
    );

    useEffect(() => {
        if (listRef.current) {
            const activeElement = listRef.current.querySelector('[data-active="true"]');
            if (activeElement && typeof activeElement.scrollIntoView === 'function') {
                activeElement.scrollIntoView({
                    block: 'nearest',
                });
            }
        }
    }, [activeIndex]);

    const showResolving =
        remote.isResolving && (remote.navigationId != null || remote.canSearchNames);

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={onClose}
            className={styles.modal}
            contentClassName={styles.modalContent}
            size="lg"
            centered={false}
            bare>
            <ModalBody>
                <div className={styles.searchWrapper}>
                    <SvgIcon
                        icon={Search}
                        size="inherit"
                        className={styles.searchIcon}
                        svgProps={{ 'aria-hidden': true }}
                    />
                    <input
                        ref={inputRef}
                        type="text"
                        className={styles.searchInput}
                        aria-label={t('quickSearch.placeholder', 'Quick navigate / search')}
                        placeholder={t('quickSearch.placeholder', 'Quick navigate / search')}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setActiveIndex(0);
                        }}
                        onKeyDown={handleKeyDown}
                    />
                    <kbd className={styles.shortcutHint}>ESC</kbd>
                </div>

                <div className={styles.listWrapper} ref={listRef}>
                    {filteredItems.length === 0 ? (
                        <div className={styles.emptyState}>
                            {showResolving
                                ? t('quickSearch.resolving', 'Looking up…')
                                : t('quickSearch.emptyState', 'No results found')}
                        </div>
                    ) : (
                        Object.entries(groupedItems).map(([groupName, { items: groupItems }]) => (
                            <div key={groupName} className={styles.groupWrapper}>
                                <div className={styles.groupHeading}>
                                    {t(`quickSearch.groups.${groupName.toLowerCase()}`, groupName)}
                                </div>
                                <div className={styles.groupList}>
                                    {groupItems.map(({ item, globalIndex }) => (
                                        <QuickSearchRow
                                            key={item.id}
                                            item={item}
                                            isActive={globalIndex === activeIndex}
                                            onSelect={handleSelect}
                                            onHover={() => setActiveIndex(globalIndex)}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </ModalBody>
        </ModalComponent>
    );
}
